import React, { useRef, useState } from 'react';

import deburr from 'lodash/deburr';
import Autosuggest from 'react-autosuggest';
import match from 'autosuggest-highlight/match';
import parse from 'autosuggest-highlight/parse';
import TextField from '@mui/material/TextField';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

interface SuggestOption {
  label: string;
  value: any;
  [key: string]: any;
}

const suggestionLimit = 25;

const RootDiv = styled('div')({
  flexGrow: 1
});

const SuggestionsList = styled('div')(({ theme }) => ({
  margin: 0,
  padding: 0,
  maxHeight: '10em',
  overflow: 'auto'
}));

function renderInputComponent(inputProps: any) {
  const { classes, inputRef, ref, ...other } = inputProps;
  return (
    <TextField
      fullWidth
      {...other}
      slotProps={{
        input: {
          inputRef: (node: any) => {
            ref(node);
            if(inputRef){
                inputRef(node);
            }
          },
          classes: {
            input: classes.input,
          },
        }
      } as any} />
  );
}

function renderSuggestion(suggestion: SuggestOption, { query, isHighlighted }: { query: string; isHighlighted: boolean }) {
  const matches = match(suggestion.label, query);
  const parts = parse(suggestion.label, matches);

  return (
    <MenuItem selected={isHighlighted} component="div">
      <div>
        {parts.map((part, index) => {
          return part.highlight ? (
            <span key={String(index)} style={{ fontWeight: 500 }}>
              {part.text}
            </span>
          ) : (
            <strong key={String(index)} style={{ fontWeight: 300 }}>
              {part.text}
            </strong>
          );
        })}
      </div>
    </MenuItem>
  );
}

function getSuggestionValue(suggestion: SuggestOption) {
  return suggestion.label;
}

interface IntegrationAutosuggestProps {
  value?: any;
  options?: SuggestOption[];
  onChange?: (value: any) => void;
  input?: {
    name?: string;
    value?: any;
    onChange?: (value: any) => void;
    [key: string]: any;
  };
  handleSelect?: (value: any) => void;
  hideUnmatched?: boolean;
  noFreeForm?: boolean;
  meta?: any;
  alwaysShowError?: boolean;
  helperText?: React.ReactNode;
  error?: any;
  label?: React.ReactNode;
  placeholder?: string;
  disabled?: boolean;
  popperClassName?: string;
  name?: string;
  [key: string]: any;
}

const IntegrationAutosuggest = (props: IntegrationAutosuggestProps) => {
  // NOTE: the original class also held `single` / `popper` in state, but neither
  // was ever read; only `suggestions` drives rendering, so the dead state is
  // omitted here without any behavioral change.
  const [suggestions, setSuggestions] = useState<SuggestOption[]>([]);

  const popperNode = useRef<any>(null);

  // `options` is optional on the public prop type; default to an empty array so
  // the .find/.filter dereferences below can never throw when a consumer omits it.
  const options = props.options ?? [];

  const getSuggestions = (value: string): SuggestOption[] => {
    const inputValue = deburr(value.trim()).toLowerCase();
    const inputLength = inputValue.length;

    if(inputLength === 0){
        return options;
    }

    var top = options.filter(suggestion => suggestion.label.slice(0, inputLength).toLowerCase() === inputValue);
    var other = options.filter(s => !top.find(x => x.value === s.value));
    if(props.hideUnmatched && (top.length + other.length) > suggestionLimit){
        return top;
    }
    return top.concat(other);
  };

  const handleSuggestionsFetchRequested = ({ value }: { value: string }) => {
    setSuggestions(getSuggestions(value));
  };

  const handleSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  const handleChange = (name: string) => (event: any, { newValue }: { newValue: any }) => {
    var option = options.find(r => r.label === newValue);
    if(option){
      newValue = option.value;
    }
    if(props.onChange){
        props.onChange(newValue);
    }
    if(props.input && props.input.onChange){
        props.input.onChange(newValue);
    }
  };

  const handleSelect = (name: string) => (event: any, { suggestionValue }: { suggestionValue: any }) => {
    if(props.handleSelect){
        props.handleSelect(suggestionValue);
    }
  };

  const getPropValue = () => {
      let value = props.value === undefined ? (props.input || {}).value : props.value;
      if(value === null || value === undefined){
        value = '';
      }
      let option = options.find(r => r.value === value);
      if(option){
          return option.label;
      }
      return value;
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      if(props.noFreeForm){
          let newValue = e.target.value;
          let option = options.find(r => r.label === newValue);
          let change = props.onChange || (props.input || {}).onChange;
          if(option){
            change(option.value);
        } else{
            change("");
        }
      }
  };

  const { meta, alwaysShowError, helperText, error } = props;

  const autosuggestProps: any = {
    renderInputComponent,
    suggestions,
    onSuggestionsFetchRequested: handleSuggestionsFetchRequested,
    onSuggestionsClearRequested: handleSuggestionsClearRequested,
    getSuggestionValue,
    renderSuggestion,
  };

  if(props.hideUnmatched){
      autosuggestProps.suggestions = suggestions.slice(0, suggestionLimit);
  }

  let errorMsg = meta ? meta.error : null;
  errorMsg = errorMsg || error;
  if(meta && meta.data && meta.data.warning){
      if(!errorMsg){
          errorMsg = meta.data.warning;
      } else {
          errorMsg += "\n" + meta.data.warning;
      }
  }
  let touched = meta && (meta.touched || meta.dirty);

  const classes = {
    input: ''
  };

  return (
      <RootDiv>
      <Autosuggest
        {...autosuggestProps}
        inputProps={{
          classes,
          label: props.label,
          name: props.input ? props.input.name : props.name,
          autoComplete: 'off',
          placeholder: props.placeholder,
          value: getPropValue(),
          onChange: handleChange('single'),
          onBlur: onBlur,
          helperText: errorMsg && (touched || alwaysShowError) ? errorMsg : helperText,
          error: errorMsg && (touched || alwaysShowError),
          inputRef: (node: any) => {
            popperNode.current = node;
        },
        disabled: props.disabled,
        InputLabelProps: {
            shrink: true
        }
        }}
        theme={{
          suggestionsList: 'suggestions-list',
          suggestion: 'suggestion',
        }}
        disabled={props.disabled}
        onSuggestionSelected={handleSelect('single')}
        shouldRenderSuggestions={() => {return true;}}
        renderSuggestionsContainer={(options: any) => (
          <Popper className={props.popperClassName} style={{zIndex: '1400'}} anchorEl={popperNode.current} open={Boolean(options.children)}>
            <Paper
              square
              {...options.containerProps}
              style={{ width: popperNode.current ? popperNode.current.clientWidth : null }}
            >
              <SuggestionsList>
                {options.children}
              </SuggestionsList>
              {suggestions.length > suggestionLimit && props.hideUnmatched ? <div style={{padding: '5px 5px 5px 10px'}}>
                  <Typography style={{color: 'rgba(0, 0, 0, 0.54)'}}>{`Showing ${suggestionLimit} of ${suggestions.length} matches.`}</Typography>
                  </div> : ''}
            </Paper>
          </Popper>
        )}
      />
      </RootDiv>
  );
};

export default IntegrationAutosuggest;
